import React, { useState } from 'react';
import { Input } from '../../../../shared/components/Input';
import { DatePicker } from '../../../../shared/components/DatePicker';
import { Select } from '../../../../shared/components/Select';
import { SectionProps, Milestone } from '../../types';

type DurationType = 'months' | 'years' | 'days';

const UNIT_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

export const TimelineSection: React.FC<SectionProps> = ({ formData, onChange }) => {
  const [useDuration, setUseDuration] = useState(true);
  const [newMilestone, setNewMilestone] = useState<Omit<Milestone, 'id'>>({
    title: '',
    description: '',
    targetDate: ''
  });

  const calculateEndDate = (startDate: Date, value: number, unit: DurationType): Date => {
    const endDate = new Date(startDate);

    if (unit === 'days') {
      endDate.setDate(startDate.getDate() + value);
    } else if (unit === 'months') {
      let targetMonth = startDate.getMonth() + value;
      let targetYear = startDate.getFullYear() + Math.floor(targetMonth / 12);
      targetMonth = targetMonth % 12;
      endDate.setFullYear(targetYear, targetMonth + 1, 0);
      const lastDayOfMonth = endDate.getDate();
      const targetDay = Math.min(startDate.getDate(), lastDayOfMonth);
      endDate.setFullYear(targetYear, targetMonth, targetDay);
    } else {
      endDate.setFullYear(startDate.getFullYear() + value);
    }

    return endDate;
  };

  const handleDurationChange = (value: string, unit: DurationType) => {
    onChange({
      target: { name: 'duration', value: { value, unit } }
    } as any);

    if (value && formData.startDate) {
      const startDate = new Date(formData.startDate);
      const durationValue = parseInt(value);

      if (!isNaN(durationValue) && durationValue > 0) {
        const endDate = calculateEndDate(startDate, durationValue, unit);
        const formattedEndDate = endDate.toISOString().split('T')[0];
        onChange({
          target: { name: 'endDate', value: formattedEndDate }
        } as React.ChangeEvent<HTMLInputElement>);
      } else {
        onChange({
          target: { name: 'endDate', value: '' }
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const handleStartDateChange = (value: string) => {
    onChange({ target: { name: 'startDate', value } } as React.ChangeEvent<HTMLInputElement>);

    if (useDuration && formData.duration.value && value) {
      const startDate = new Date(value);
      const durationValue = parseInt(formData.duration.value);
      if (!isNaN(durationValue) && durationValue > 0) {
        const endDate = calculateEndDate(startDate, durationValue, formData.duration.unit);
        onChange({
          target: { name: 'endDate', value: endDate.toISOString().split('T')[0] }
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const handleMilestoneChange = (field: keyof Omit<Milestone, 'id'>, value: string) => {
    setNewMilestone(prev => ({ ...prev, [field]: value }));
  };

  const handleAddMilestone = () => {
    if (newMilestone.title && newMilestone.targetDate) {
      const milestone: Milestone = {
        ...newMilestone,
        id: Date.now().toString()
      };
      onChange({
        target: { name: 'milestones', value: [...(formData.milestones || []), milestone] }
      } as any);
      setNewMilestone({ title: '', description: '', targetDate: '' });
    }
  };

  const handleDeleteMilestone = (id: string) => {
    onChange({
      target: { name: 'milestones', value: (formData.milestones || []).filter(m => m.id !== id) }
    } as any);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium font-heading text-blueprint-blue">Project Timeline</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-grey">Use Duration</span>
            <button
              type="button"
              onClick={() => setUseDuration(!useDuration)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                useDuration ? 'bg-primary' : 'bg-[#E8EAED]'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                  useDuration ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <DatePicker
              label="Start Date"
              value={formData.startDate}
              onChange={handleStartDateChange}
            />
          </div>

          {useDuration ? (
            <div className="flex space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-text-grey mb-1">
                  Duration
                </label>
                <Input
                  type="number"
                  value={formData.duration.value}
                  onChange={(e) => handleDurationChange(e.target.value, formData.duration.unit)}
                  min="1"
                  placeholder="Enter duration"
                  className="w-full"
                />
              </div>
              <div className="w-36">
                <Select
                  label="Unit"
                  value={formData.duration.unit}
                  options={UNIT_OPTIONS}
                  onChange={(value) => handleDurationChange(formData.duration.value, value as DurationType)}
                />
              </div>
            </div>
          ) : (
            <DatePicker
              label="End Date"
              value={formData.endDate}
              onChange={(value) => onChange({ target: { name: 'endDate', value } } as React.ChangeEvent<HTMLInputElement>)}
            />
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-medium font-heading text-blueprint-blue mb-6">Milestones</h3>

        {/* Add New Milestone */}
        <div className="mb-8 border-b border-[#E2E5EA] pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <label className="block text-sm font-medium text-text-grey mb-1">
                Milestone Title
              </label>
              <Input
                type="text"
                value={newMilestone.title}
                onChange={(e) => handleMilestoneChange('title', e.target.value)}
                placeholder="Enter milestone title"
                className="w-full"
              />
            </div>
            <DatePicker
              label="Target Date"
              value={newMilestone.targetDate}
              onChange={(value) => handleMilestoneChange('targetDate', value)}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-grey mb-2">
              Description
            </label>
            <textarea
              value={newMilestone.description}
              onChange={(e) => handleMilestoneChange('description', e.target.value)}
              placeholder="Enter milestone description"
              className="w-full px-3 py-2 border border-[#E2E5EA] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={3}
            />
          </div>
          <button
            type="button"
            onClick={handleAddMilestone}
            disabled={!newMilestone.title || !newMilestone.targetDate}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Milestone
          </button>
        </div>

        {/* Milestone List */}
        <div className="space-y-4">
          {(formData.milestones || []).map((milestone) => (
            <div
              key={milestone.id}
              className="flex items-start justify-between p-4 border border-[#E2E5EA] rounded-lg hover:border-primary/20 transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-base font-medium text-text-grey">{milestone.title}</h4>
                  <span className="text-sm text-heritage-grey">{milestone.targetDate}</span>
                </div>
                {milestone.description && (
                  <p className="text-sm text-text-grey">{milestone.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleDeleteMilestone(milestone.id)}
                className="ml-4 p-1 text-heritage-grey hover:text-readable-red"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
          {(!formData.milestones || formData.milestones.length === 0) && (
            <p className="text-center text-heritage-grey text-sm py-4">No milestones added yet</p>
          )}
        </div>
      </div>
    </div>
  );
};
